import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';
import { fetchCompanies, updateCompanyTracked, fetchConsensusValuation, fetchLeaderboard, apiFetch } from '../utils/api';
import { formatMarketCap } from '../utils/formatters';
import { getLeaderboardCache, setLeaderboardCache } from '../utils/leaderboardCache';
import DebugPipelineFreshness from './DebugPipelineFreshness';
import DebugBulkIngest from './DebugBulkIngest';

// Sector abbreviations for better display
const sectorAbbreviations = {
  'Consumer Discretionary': 'Consumer Disc.',
  'Information Technology': 'IT',
  'Health Care': 'Healthcare',
  'Communication Services': 'Comm. Services',
  'Consumer Staples': 'Consumer Staples',
  'Financial Services': 'Financial',
  'Industrials': 'Industrials',
  'Materials': 'Materials',
  'Real Estate': 'Real Estate',
  'Utilities': 'Utilities',
  'Energy': 'Energy'
};

// Color mapping for sectors (with reduced opacity)
// Colors chosen to maximize visual distinction
const sectorColors = {
  // Existing sectors
  'Information Technology': { bg: 'rgba(59, 130, 246, 0.2)', text: '#3b82f6' }, // Blue
  'Technology': { bg: 'rgba(59, 130, 246, 0.2)', text: '#3b82f6' }, // Blue (same as IT)
  'Health Care': { bg: 'rgba(16, 185, 129, 0.2)', text: '#10b981' }, // Green
  'Healthcare': { bg: 'rgba(16, 185, 129, 0.2)', text: '#10b981' }, // Green (same as Health Care)
  'Financial Services': { bg: 'rgba(245, 158, 11, 0.2)', text: '#f59e0b' }, // Amber
  'Consumer Discretionary': { bg: 'rgba(239, 68, 68, 0.2)', text: '#ef4444' }, // Red
  'Communication Services': { bg: 'rgba(139, 92, 246, 0.2)', text: '#8b5cf6' }, // Purple
  'Industrials': { bg: 'rgba(6, 182, 212, 0.2)', text: '#06b6d4' }, // Cyan
  'Consumer Staples': { bg: 'rgba(132, 204, 22, 0.2)', text: '#84cc16' }, // Lime
  'Energy': { bg: 'rgba(249, 115, 22, 0.2)', text: '#f97316' }, // Orange
  'Utilities': { bg: 'rgba(20, 184, 166, 0.2)', text: '#14b8a6' }, // Teal
  'Real Estate': { bg: 'rgba(236, 72, 153, 0.2)', text: '#ec4899' }, // Pink
  'Materials': { bg: 'rgba(99, 102, 241, 0.2)', text: '#6366f1' }, // Indigo
  
  // New sectors - Aerospace & Defense
  'Aerospace & Defense': { bg: 'rgba(30, 64, 175, 0.2)', text: '#1e40af' }, // Navy Blue
  
  // Airlines
  'Airlines': { bg: 'rgba(168, 85, 247, 0.2)', text: '#a855f7' }, // Violet
  
  // Auto Components
  'Auto Components': { bg: 'rgba(220, 38, 127, 0.2)', text: '#dc267f' }, // Deep Pink
  
  // Automobiles
  'Automobiles': { bg: 'rgba(194, 65, 12, 0.2)', text: '#c2410c' }, // Dark Orange
  
  // Banking
  'Banking': { bg: 'rgba(217, 119, 6, 0.2)', text: '#d97706' }, // Dark Amber
  
  // Beverages
  'Beverages': { bg: 'rgba(34, 197, 94, 0.2)', text: '#22c55e' }, // Emerald Green
  
  // Biotechnology
  'Biotechnology': { bg: 'rgba(5, 150, 105, 0.2)', text: '#059669' }, // Dark Green
  
  // Building
  'Building': { bg: 'rgba(120, 113, 108, 0.2)', text: '#78716c' }, // Stone Gray
  
  // Chemicals
  'Chemicals': { bg: 'rgba(147, 51, 234, 0.2)', text: '#9333ea' }, // Deep Purple
  
  // Commercial Services & Supplies
  'Commercial Services & Supplies': { bg: 'rgba(79, 70, 229, 0.2)', text: '#4f46e5' }, // Indigo Blue
  
  // Communications
  'Communications': { bg: 'rgba(192, 38, 211, 0.2)', text: '#c026d3' }, // Fuchsia
  
  // Construction
  'Construction': { bg: 'rgba(180, 83, 9, 0.2)', text: '#b45309' }, // Brown Orange
  
  // Consumer products
  'Consumer products': { bg: 'rgba(225, 29, 72, 0.2)', text: '#e11d48' }, // Rose Red
  
  // Distributors
  'Distributors': { bg: 'rgba(14, 165, 233, 0.2)', text: '#0ea5e9' }, // Sky Blue
  
  // Diversified Consumer Services
  'Diversified Consumer Services': { bg: 'rgba(251, 146, 60, 0.2)', text: '#fb923c' }, // Light Orange
  
  // Electrical Equipment
  'Electrical Equipment': { bg: 'rgba(234, 179, 8, 0.2)', text: '#eab308' }, // Yellow
  
  // Food Products
  'Food Products': { bg: 'rgba(74, 222, 128, 0.2)', text: '#4ade80' }, // Light Green
  
  // Hotels, Restaurants & Leisure
  'Hotels, Restaurants & Leisure': { bg: 'rgba(251, 113, 133, 0.2)', text: '#fb7185' }, // Rose Pink
  
  // Industrial Conglomerates
  'Industrial Conglomerates': { bg: 'rgba(6, 182, 212, 0.2)', text: '#06b6d4' }, // Cyan (related to Industrials)
  
  // Insurance
  'Insurance': { bg: 'rgba(202, 138, 4, 0.2)', text: '#ca8804' }, // Dark Yellow
  
  // Leisure Products
  'Leisure Products': { bg: 'rgba(244, 63, 94, 0.2)', text: '#f43f5e' }, // Pink Red
  
  // Life Sciences Tools & Services
  'Life Sciences Tools & Services': { bg: 'rgba(8, 145, 178, 0.2)', text: '#0891b2' }, // Dark Cyan (distinct from Biotechnology and Industrials)
  
  // Logistics & Transportation
  'Logistics & Transportation': { bg: 'rgba(37, 99, 235, 0.2)', text: '#2563eb' }, // Royal Blue
  
  // Machinery
  'Machinery': { bg: 'rgba(71, 85, 105, 0.2)', text: '#475569' }, // Slate Gray
  
  // Media
  'Media': { bg: 'rgba(168, 85, 247, 0.2)', text: '#a855f7' }, // Violet
  
  // Metals & Mining
  'Metals & Mining': { bg: 'rgba(101, 163, 13, 0.2)', text: '#65a30d' }, // Olive Green
  
  // Packaging
  'Packaging': { bg: 'rgba(161, 98, 7, 0.2)', text: '#a16207' }, // Dark Brown
  
  // Paper & Forest
  'Paper & Forest': { bg: 'rgba(22, 163, 74, 0.2)', text: '#16a34a' }, // Forest Green
  
  // Pharmaceuticals
  'Pharmaceuticals': { bg: 'rgba(34, 197, 94, 0.2)', text: '#22c55e' }, // Emerald
  
  // Professional Services
  'Professional Services': { bg: 'rgba(124, 58, 237, 0.2)', text: '#7c3aed' }, // Purple Blue
  
  // Retail
  'Retail': { bg: 'rgba(219, 39, 119, 0.2)', text: '#db2777' }, // Hot Pink
  
  // Road & Rail
  'Road & Rail': { bg: 'rgba(30, 58, 138, 0.2)', text: '#1e3a8a' }, // Dark Blue
  
  // Semiconductors
  'Semiconductors': { bg: 'rgba(59, 130, 246, 0.2)', text: '#3b82f6' }, // Blue (same as Technology)
  
  // Telecommunication
  'Telecommunication': { bg: 'rgba(147, 51, 234, 0.2)', text: '#9333ea' }, // Deep Purple
  
  // Textiles, Apparel & Luxury Goods
  'Textiles, Apparel & Luxury Goods': { bg: 'rgba(236, 72, 153, 0.2)', text: '#ec4899' }, // Pink (same as Real Estate - related sectors)
  
  // Tobacco
  'Tobacco': { bg: 'rgba(55, 65, 81, 0.2)', text: '#374151' }, // Dark Gray
  
  // Trading Companies & Distributors
  'Trading Companies & Distributors': { bg: 'rgba(79, 70, 229, 0.2)', text: '#4f46e5' }, // Indigo Blue
  
  // Transportation Infrastructure
  'Transportation Infrastructure': { bg: 'rgba(30, 64, 175, 0.2)', text: '#1e40af' } // Navy Blue
};

// Helper function to get sector color
const getSectorColor = (sector) => {
  return sectorColors[sector] || { bg: 'rgba(107, 114, 128, 0.2)', text: '#6b7280' }; // Default gray
};

// Market cap intervals (in dollars)
const MARKET_CAP_INTERVALS = {
  'all': { label: 'All', min: null, max: null },
  'mega': { label: 'Mega Cap (>$200B)', min: 200000000000, max: null },
  'large': { label: 'Large Cap ($10B-$200B)', min: 10000000000, max: 200000000000 },
  'mid': { label: 'Mid Cap ($2B-$10B)', min: 2000000000, max: 10000000000 },
  'small': { label: 'Small Cap ($300M-$2B)', min: 300000000, max: 2000000000 },
  'micro': { label: 'Micro Cap (<$300M)', min: null, max: 300000000 }
};

// Helper function to filter stocks by market cap interval
const filterByMarketCap = (stock, filterValue) => {
  if (filterValue === 'all') {
    return true;
  }
  
  const interval = MARKET_CAP_INTERVALS[filterValue];
  if (!interval) {
    return true; // Unknown filter, show all
  }
  
  const marketCap = stock.market_cap;
  
  // If market cap is null/undefined, exclude from filtered results (unless "all" is selected)
  if (marketCap === null || marketCap === undefined) {
    return false;
  }
  
  const marketCapValue = typeof marketCap === 'number' ? marketCap : parseFloat(marketCap);
  
  // Check if market cap falls within the interval
  if (interval.min !== null && marketCapValue < interval.min) {
    return false;
  }
  if (interval.max !== null && marketCapValue >= interval.max) {
    return false;
  }
  
  return true;
};

const WATCHLIST_SESSION_KEY = 'nightcrawler_watchlist_tracked';

function readWatchlistSessionCache() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(WATCHLIST_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeWatchlistSessionCache(stocks) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (!Array.isArray(stocks) || stocks.length === 0) {
      sessionStorage.removeItem(WATCHLIST_SESSION_KEY);
      return;
    }
    sessionStorage.setItem(WATCHLIST_SESSION_KEY, JSON.stringify(stocks));
  } catch {
    // Quota or private mode — ignore
  }
}

const DASHBOARD_TAB_ORDER = ['leaderboard', 'watchlist', 'all-stocks', 'debug', 'more'];

const DASHBOARD_TABS = [
  { id: 'leaderboard', ariaLabel: 'Leaderboard' },
  { id: 'watchlist', ariaLabel: 'Watchlist' },
  { id: 'all-stocks', ariaLabel: 'All stocks' },
  { id: 'debug', ariaLabel: 'Debug' },
  { id: 'more', ariaLabel: 'More views' },
];

function DashboardTabSvg({ variant }) {
  const svgProps = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
  };
  switch (variant) {
    case 'leaderboard':
      return (
        <svg {...svgProps}>
          <path
            d="M4 20V10M12 20V4M20 20v-8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'watchlist':
      return (
        <svg {...svgProps}>
          <path
            d="M12 2l3 7h7l-5.5 4 2 7L12 17l-6.5 4 2-7L2 9h7z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'all-stocks':
      return (
        <svg {...svgProps}>
          <rect x="3" y="4" width="18" height="16" rx="1" stroke="currentColor" strokeWidth="2" />
          <path d="M3 9h18M9 4v16" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case 'debug':
      return (
        <svg {...svgProps}>
          <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
          <path
            d="M7 10l2 2-2 2M11 14h6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'more':
      return (
        <svg {...svgProps}>
          <rect x="4" y="4" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="2" />
          <rect x="14" y="4" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="2" />
          <rect x="4" y="14" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="2" />
          <rect x="14" y="14" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    default:
      return null;
  }
}

function Dashboard() {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState(() => readWatchlistSessionCache() || []);
  const [watchlistLoading, setWatchlistLoading] = useState(() => !readWatchlistSessionCache());
  const [watchlistError, setWatchlistError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, technology, healthcare, etc.
  const [valuations, setValuations] = useState({}); // Map of ticker -> valuation data
  
  // All Stocks section state
  const [allStocks, setAllStocks] = useState([]);
  const [allStocksLoading, setAllStocksLoading] = useState(true);
  const [allStocksError, setAllStocksError] = useState(null);
  const [sortColumn, setSortColumn] = useState('ticker');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilterAll, setSectorFilterAll] = useState('all');
  const [allStocksMarketCapFilter, setAllStocksMarketCapFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Leaderboard section state
  const [leaderboardStocks, setLeaderboardStocks] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [leaderboardSearchQuery, setLeaderboardSearchQuery] = useState('');
  const [leaderboardSectorFilter, setLeaderboardSectorFilter] = useState('all');
  const [leaderboardMarketCapFilter, setLeaderboardMarketCapFilter] = useState('all');
  const [leaderboardCurrentPage, setLeaderboardCurrentPage] = useState(1);
  const [leaderboardItemsPerPage, setLeaderboardItemsPerPage] = useState(50);
  const [activeDashboardTab, setActiveDashboardTab] = useState('leaderboard');

  const focusDashboardTab = (tabId) => {
    setActiveDashboardTab(tabId);
    requestAnimationFrame(() => {
      document.getElementById(`dashboard-tab-${tabId}`)?.focus();
    });
  };

  const handleDashboardTabKeyDown = (e, tabId) => {
    const i = DASHBOARD_TAB_ORDER.indexOf(tabId);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = DASHBOARD_TAB_ORDER[(i + 1) % DASHBOARD_TAB_ORDER.length];
      focusDashboardTab(next);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next =
        DASHBOARD_TAB_ORDER[(i - 1 + DASHBOARD_TAB_ORDER.length) % DASHBOARD_TAB_ORDER.length];
      focusDashboardTab(next);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusDashboardTab(DASHBOARD_TAB_ORDER[0]);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusDashboardTab(DASHBOARD_TAB_ORDER[DASHBOARD_TAB_ORDER.length - 1]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setWatchlistError(null);
        const hasSessionWatchlist = !!readWatchlistSessionCache()?.length;
        if (!hasSessionWatchlist) {
          setWatchlistLoading(true);
        }

        logger.log('Fetching data...', new Date().toISOString(), 'Backend connected!');

        const stocksData = await apiFetch('/companies?limit=50&tracked=true');
        setStocks(stocksData);
        writeWatchlistSessionCache(stocksData);
      } catch (err) {
        setWatchlistError('Failed to load watchlist: ' + err.message);
      } finally {
        setWatchlistLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch consensus valuations for watchlist stocks
  useEffect(() => {
    const fetchValuations = async () => {
      if (stocks.length === 0) return;
      
      try {
        // Fetch valuations in parallel for all stocks
        const valuationPromises = stocks.map(async (stock) => {
          try {
            const valuation = await fetchConsensusValuation(stock.ticker);
            return { ticker: stock.ticker, valuation };
          } catch (err) {
            logger.warn(`Failed to fetch valuation for ${stock.ticker}:`, err);
            return { ticker: stock.ticker, valuation: null };
          }
        });
        
        const results = await Promise.all(valuationPromises);
        const valuationsMap = {};
        results.forEach(({ ticker, valuation }) => {
          valuationsMap[ticker] = valuation;
        });
        setValuations(valuationsMap);
      } catch (err) {
        logger.error('Error fetching valuations:', err);
      }
    };

    fetchValuations();
  }, [stocks]);

  // Fetch all stocks for the All Stocks section
  useEffect(() => {
    const fetchAllStocks = async () => {
      try {
        setAllStocksLoading(true);
        setAllStocksError(null);
        
        // Fetch all stocks with higher limit
        const allStocksData = await fetchCompanies({ limit: 1000, offset: 0 });
        setAllStocks(allStocksData);
        setAllStocksLoading(false);
      } catch (err) {
        setAllStocksError('Failed to load all stocks: ' + err.message);
        setAllStocksLoading(false);
      }
    };

    fetchAllStocks();
  }, []);

  // Fetch all stocks and their consensus valuations for Leaderboard
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      const overallStart = performance.now();
      try {
        setLeaderboardLoading(true);
        setLeaderboardError(null);
        
        // Check cache first
        const cachedData = getLeaderboardCache();
        if (cachedData) {
          logger.log('Using cached leaderboard data');
          setLeaderboardStocks(cachedData);
          setLeaderboardLoading(false);
          return;
        }
        
        // Cache miss - fetch from optimized endpoint
        logger.log('Fetching fresh leaderboard data from optimized endpoint');
        const fetchStart = performance.now();
        
        const leaderboardData = await fetchLeaderboard({ limit: 1000, offset: 0 });
        
        const fetchTime = performance.now() - fetchStart;
        logger.log(`[TIMING] Fetched leaderboard in ${fetchTime.toFixed(2)}ms`);
        
        // Map to expected format (backend already filters and sorts)
        const stocksWithValuations = leaderboardData.map(stock => ({
          ticker: stock.ticker,
          name: stock.name,
          sector: stock.sector,
          exchange: stock.exchange,
          market_cap: stock.market_cap,
          current_price: stock.current_price,
          last_updated: stock.last_updated,
          tracked: stock.tracked,
          valuation_score: stock.valuation_score ?? null,
          interpretation: stock.interpretation ?? null,
          target_date: stock.target_date ?? null
        }));
        
        const overallTime = performance.now() - overallStart;
        logger.log(`[TIMING] Total leaderboard load time: ${overallTime.toFixed(2)}ms (fetch: ${fetchTime.toFixed(2)}ms)`);
        
        // Store in cache
        setLeaderboardCache(stocksWithValuations);
        setLeaderboardStocks(stocksWithValuations);
        setLeaderboardLoading(false);
      } catch (err) {
        const overallTime = performance.now() - overallStart;
        logger.error(`[TIMING] Leaderboard failed after ${overallTime.toFixed(2)}ms:`, err);
        setLeaderboardError('Failed to load leaderboard data: ' + err.message);
        setLeaderboardLoading(false);
      }
    };

    fetchLeaderboardData();
  }, []);

  // Filter stocks by sector.
  const filteredStocks = filter === 'all' 
    ? stocks 
    : stocks.filter(stock => stock.sector?.toLowerCase() === filter.toLowerCase());

  // Get unique sectors for filter dropdown.
  const uniqueSectors = Array.from(new Set(stocks.map(stock => stock.sector).filter(Boolean))).sort();
  const sectors = ['all', ...uniqueSectors];

  // Handle stock card click.
  const handleStockClick = (ticker, event) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(`/stock/${ticker}`);
  };

  // Handle sorting for All Stocks table
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter and sort all stocks
  const getFilteredAndSortedStocks = () => {
    let filtered = [...allStocks];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(stock => 
        stock.ticker.toLowerCase().includes(query) ||
        stock.name.toLowerCase().includes(query)
      );
    }

    // Apply sector filter
    if (sectorFilterAll !== 'all') {
      filtered = filtered.filter(stock => 
        stock.sector?.toLowerCase() === sectorFilterAll.toLowerCase()
      );
    }

    // Apply market cap filter
    if (allStocksMarketCapFilter !== 'all') {
      filtered = filtered.filter(stock => filterByMarketCap(stock, allStocksMarketCapFilter));
    }

    // Apply sorting
    // Create a copy and sort to ensure we don't mutate the original
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'ticker':
          aVal = a.ticker || '';
          bVal = b.ticker || '';
          break;
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'sector':
          aVal = a.sector || '';
          bVal = b.sector || '';
          break;
        case 'price':
          aVal = a.current_price ?? -Infinity;
          bVal = b.current_price ?? -Infinity;
          break;
        case 'market_cap':
          aVal = a.market_cap ?? -Infinity;
          bVal = b.market_cap ?? -Infinity;
          break;
        case 'tracked':
          aVal = (a.tracked || false) ? 1 : 0;
          bVal = (b.tracked || false) ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (sortColumn === 'price' || sortColumn === 'market_cap') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortColumn === 'tracked') {
        return sortDirection === 'asc' ? bVal - aVal : aVal - bVal;
      } else {
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }
    });

    return sorted;
  };

  // Get paginated stocks
  const getPaginatedStocks = () => {
    const filtered = getFilteredAndSortedStocks();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      stocks: filtered.slice(startIndex, endIndex),
      total: filtered.length
    };
  };

  // Filter leaderboard stocks by search query and sector
  const getFilteredLeaderboardStocks = () => {
    let filtered = [...leaderboardStocks];

    // Apply search filter
    if (leaderboardSearchQuery) {
      const query = leaderboardSearchQuery.toLowerCase();
      filtered = filtered.filter(stock => 
        stock.ticker.toLowerCase().includes(query) ||
        stock.name.toLowerCase().includes(query)
      );
    }

    // Apply sector filter
    if (leaderboardSectorFilter !== 'all') {
      filtered = filtered.filter(stock => 
        stock.sector?.toLowerCase() === leaderboardSectorFilter.toLowerCase()
      );
    }

    // Apply market cap filter
    if (leaderboardMarketCapFilter !== 'all') {
      filtered = filtered.filter(stock => filterByMarketCap(stock, leaderboardMarketCapFilter));
    }

    // Maintain ranking order (already sorted by valuation_score descending)
    return filtered;
  };

  // Get paginated leaderboard stocks
  const getPaginatedLeaderboardStocks = () => {
    const filtered = getFilteredLeaderboardStocks();
    const startIndex = (leaderboardCurrentPage - 1) * leaderboardItemsPerPage;
    const endIndex = startIndex + leaderboardItemsPerPage;
    return {
      stocks: filtered.slice(startIndex, endIndex),
      total: filtered.length
    };
  };

  // Handle tracked status toggle
  const handleToggleTracked = async (ticker, currentTracked) => {
    try {
      const newTracked = !currentTracked;
      const updatedCompany = await updateCompanyTracked(ticker, newTracked);
      
      // Update allStocks state
      setAllStocks(allStocks.map(stock => 
        stock.ticker === ticker ? { ...stock, tracked: newTracked } : stock
      ));
      
      // Update watchlist: add if tracked, remove if untracked
      if (newTracked) {
        setStocks((prev) => {
          const isAlreadyInWatchlist = prev.some((stock) => stock.ticker === ticker);
          if (!isAlreadyInWatchlist && updatedCompany) {
            const next = [...prev, updatedCompany];
            writeWatchlistSessionCache(next);
            return next;
          }
          return prev;
        });
      } else {
        setStocks((prev) => {
          const next = prev.filter((stock) => stock.ticker !== ticker);
          writeWatchlistSessionCache(next);
          return next;
        });
      }
    } catch (err) {
      logger.error('Failed to update tracked status:', err);
      alert('Failed to update tracked status. Please try again.');
    }
  };

  // Helper function to get CSS class from interpretation
  const getInterpretationClass = (interpretation) => {
    if (!interpretation) return '';
    // Convert interpretation to CSS class name
    const classMap = {
      'Very Undervalued': 'very-undervalued',
      'Undervalued': 'undervalued',
      'Fairly Priced': 'fairly-priced',
      'Overvalued': 'overvalued',
      'Very Overvalued': 'very-overvalued'
    };
    return classMap[interpretation] || '';
  };

  // Helper function to get colors for interpretation
  const getInterpretationColors = (interpretation) => {
    if (!interpretation) return { bg: '#f5f5f5', text: '#666' };
    
    // Normalize the interpretation string (trim and handle case)
    const normalized = interpretation.trim();
    const lowerNormalized = normalized.toLowerCase();
    
    const colorMap = {
      // New labels
      'Very Undervalued': { bg: '#c8e6c9', text: '#2e7d32' },
      'Undervalued': { bg: '#e8f5e9', text: '#388e3c' },
      'Fairly Priced': { bg: '#f5f5f5', text: '#666' },
      'Overvalued': { bg: '#ffcdd2', text: '#c62828' },
      'Very Overvalued': { bg: '#ffebee', text: '#c62828' },
      // Old labels (for backward compatibility)
      'Strong undervaluation': { bg: '#c8e6c9', text: '#2e7d32' },
      'Moderate opportunity': { bg: '#e8f5e9', text: '#388e3c' },
      'Fairly valued': { bg: '#f5f5f5', text: '#666' },
      'Strong overvaluation': { bg: '#ffebee', text: '#c62828' }
    };
    
    // Try exact match first (case-sensitive)
    if (colorMap[normalized]) {
      return colorMap[normalized];
    }
    
    // Try case-insensitive match
    for (const [key, value] of Object.entries(colorMap)) {
      if (key.toLowerCase() === lowerNormalized) {
        return value;
      }
    }
    
    // Fallback: check if it contains keywords (most flexible)
    if (lowerNormalized.includes('very undervalued') || lowerNormalized.includes('strong undervaluation')) {
      return { bg: '#c8e6c9', text: '#2e7d32' };
    } else if (lowerNormalized.includes('undervalued') && !lowerNormalized.includes('very') && !lowerNormalized.includes('strong')) {
      return { bg: '#e8f5e9', text: '#388e3c' };
    } else if (lowerNormalized.includes('moderate opportunity')) {
      return { bg: '#e8f5e9', text: '#388e3c' };
    } else if (lowerNormalized.includes('fairly priced') || lowerNormalized.includes('fairly valued')) {
      return { bg: '#f5f5f5', text: '#666' };
    } else if (lowerNormalized.includes('very overvalued') || lowerNormalized.includes('strong overvaluation')) {
      return { bg: '#ffebee', text: '#c62828' };
    } else if (lowerNormalized.includes('overvalued')) {
      return { bg: '#ffcdd2', text: '#c62828' };
    }
    
    // Default fallback
    logger.warn(`Unknown interpretation: "${interpretation}"`);
    return { bg: '#f5f5f5', text: '#666' };
  };

  // Get unique sectors for All Stocks filter
  const uniqueAllSectors = Array.from(new Set(allStocks.map(stock => stock.sector).filter(Boolean))).sort();
  const allSectors = ['all', ...uniqueAllSectors];
  
  // Get unique sectors for Leaderboard filter
  const uniqueLeaderboardSectors = Array.from(new Set(leaderboardStocks.map(stock => stock.sector).filter(Boolean))).sort();
  const leaderboardSectors = ['all', ...uniqueLeaderboardSectors];
  
  // Get paginated leaderboard stocks
  const { stocks: paginatedLeaderboardStocks, total: leaderboardTotalFiltered } = getPaginatedLeaderboardStocks();
  const leaderboardTotalPages = Math.ceil(leaderboardTotalFiltered / leaderboardItemsPerPage);
  
  // Get pagination info for All Stocks
  const { stocks: paginatedStocks, total: totalFiltered } = getPaginatedStocks();
  const totalPages = Math.ceil(totalFiltered / itemsPerPage);

  return (
    <div className="dashboard">
      <div className="dashboard-tablist" role="tablist" aria-label="Dashboard views">
        {DASHBOARD_TABS.map(({ id, ariaLabel }) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`dashboard-tab-${id}`}
            aria-selected={activeDashboardTab === id}
            aria-controls={`dashboard-panel-${id}`}
            tabIndex={activeDashboardTab === id ? 0 : -1}
            className={`dashboard-tab${activeDashboardTab === id ? ' dashboard-tab--active' : ''}`}
            onClick={() => setActiveDashboardTab(id)}
            onKeyDown={(e) => handleDashboardTabKeyDown(e, id)}
            aria-label={ariaLabel}
            title={ariaLabel}
          >
            <DashboardTabSvg variant={id} />
          </button>
        ))}
      </div>

      <div
        id="dashboard-panel-leaderboard"
        role="tabpanel"
        aria-labelledby="dashboard-tab-leaderboard"
        hidden={activeDashboardTab !== 'leaderboard'}
        className="dashboard-tabpanel"
      >
      <div className="component">
        <div className="stocks-header">
          <h2>Leaderboard</h2>
          <div className="filter-controls">
            <input
              type="text"
              placeholder="Search by ticker or name..."
              value={leaderboardSearchQuery}
              onChange={(e) => {
                setLeaderboardSearchQuery(e.target.value);
                setLeaderboardCurrentPage(1);
              }}
              className="search-input"
              style={{
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.9rem',
                width: '250px'
              }}
            />
            <select 
              id="sector-filter-leaderboard"
              value={leaderboardSectorFilter} 
              onChange={(e) => {
                setLeaderboardSectorFilter(e.target.value);
                setLeaderboardCurrentPage(1);
              }}
              className="sector-filter"
              style={{ width: '180px' }}
            >
              {leaderboardSectors.map(sector => (
                <option key={sector} value={sector}>
                  {sector === 'all' ? 'All Sectors' : sector}
                </option>
              ))}
            </select>
            <select 
              id="market-cap-filter-leaderboard"
              value={leaderboardMarketCapFilter} 
              onChange={(e) => {
                setLeaderboardMarketCapFilter(e.target.value);
                setLeaderboardCurrentPage(1);
              }}
              className="sector-filter"
              style={{ width: '200px' }}
            >
              {Object.entries(MARKET_CAP_INTERVALS).map(([key, interval]) => (
                <option key={key} value={key}>
                  {interval.label}
                </option>
              ))}
            </select>
            <select
              id="entries-per-page-leaderboard"
              value={leaderboardItemsPerPage}
              onChange={(e) => {
                setLeaderboardItemsPerPage(Number(e.target.value));
                setLeaderboardCurrentPage(1);
              }}
              className="entries-per-page-dropdown"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>

        {leaderboardLoading ? (
          <div className="loading">Loading leaderboard...</div>
        ) : leaderboardError ? (
          <div className="error">{leaderboardError}</div>
        ) : leaderboardTotalFiltered === 0 ? (
          <p className="no-price">No stocks with consensus valuations found.</p>
        ) : (
          <>
            <div className="table-container">
              <table className="all-stocks-table">
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>Rank</th>
                    <th>Ticker</th>
                    <th>Name</th>
                    <th style={{ width: '120px', whiteSpace: 'nowrap' }}>Market Cap</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLeaderboardStocks.map((stock, index) => {
                    const globalRank = (leaderboardCurrentPage - 1) * leaderboardItemsPerPage + index + 1;
                    return (
                      <tr key={stock.ticker} className="table-row">
                        <td className="table-cell" style={{ fontWeight: 'bold', textAlign: 'left', width: '60px' }}>
                          {globalRank}
                        </td>
                        <td className="table-cell">
                          <span 
                            style={{ cursor: 'pointer', color: '#3b82f6', fontWeight: 'bold' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/stock/${stock.ticker}`);
                            }}
                          >
                            {stock.ticker}
                          </span>
                        </td>
                        <td className="table-cell">{stock.name}</td>
                        <td className="table-cell" style={{ width: '120px' }}>
                          {formatMarketCap(stock.market_cap)}
                        </td>
                        <td className="table-cell" style={{ fontWeight: '600' }}>
                          {stock.valuation_score !== null 
                            ? stock.valuation_score.toFixed(3) 
                            : 'N/A'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {leaderboardTotalPages > 1 && (
              <div className="pagination-controls" style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid #f0f0f0'
              }}>
                <div className="pagination-arrows" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.9rem', color: '#666', marginRight: '0.5rem' }}>
                    Page {leaderboardCurrentPage} of {leaderboardTotalPages}
                  </span>
                  <button
                    onClick={() => setLeaderboardCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={leaderboardCurrentPage === 1}
                    className="pagination-arrow-button"
                    aria-label="Previous page"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => setLeaderboardCurrentPage(prev => Math.min(leaderboardTotalPages, prev + 1))}
                    disabled={leaderboardCurrentPage === leaderboardTotalPages}
                    className="pagination-arrow-button"
                    aria-label="Next page"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </div>

      <div
        id="dashboard-panel-watchlist"
        role="tabpanel"
        aria-labelledby="dashboard-tab-watchlist"
        hidden={activeDashboardTab !== 'watchlist'}
        className="dashboard-tabpanel"
      >
      <div className="component">
        <div className="stocks-header">
          <h2>Watchlist</h2>
          <div className="filter-controls">
            <select 
              id="sector-filter"
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="sector-filter"
            >
              {sectors.map(sector => (
                <option key={sector} value={sector}>
                  {sector === 'all' ? 'All Sectors' : sector}
                </option>
              ))}
            </select>
          </div>
        </div>

        {watchlistError ? <div className="error">{watchlistError}</div> : null}

        {watchlistLoading && stocks.length === 0 ? (
          <div className="loading">Loading watchlist...</div>
        ) : filteredStocks.length === 0 ? (
          <p className="no-price">No stocks.</p>
        ) : (
          <div className="stocks-grid">
            {filteredStocks.map(stock => {
              const valuation = valuations[stock.ticker];
              const interpretation = valuation?.interpretation;
              const interpretationClass = getInterpretationClass(interpretation);
              const colors = interpretation ? getInterpretationColors(interpretation) : null;
              
              return (
                <div 
                  key={stock.ticker} 
                  className="stock-card clickable"
                  onClick={(event) => handleStockClick(stock.ticker, event)}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  <div className="stock-header">
                    <div className="stock-info">
                      <h3>{stock.ticker}</h3>
                      <p className="stock-name">{stock.name}</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginTop: '0.25rem' }}>
                        {interpretation && colors && (
                          <span 
                            className={`consensus-interpretation consensus-interpretation-${interpretationClass}`}
                            style={{
                              padding: '0.4rem 0.75rem',
                              borderRadius: '16px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              whiteSpace: 'nowrap',
                              backgroundColor: colors.bg,
                              color: colors.text,
                              display: 'inline-block',
                              textTransform: 'uppercase'
                            }}
                          >
                            {interpretation}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="stock-price">
                      {stock.current_price ? (
                        <span className="price">${stock.current_price.toFixed(2)}</span>
                      ) : (
                        <span className="no-price">No price data</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>

      <div
        id="dashboard-panel-all-stocks"
        role="tabpanel"
        aria-labelledby="dashboard-tab-all-stocks"
        hidden={activeDashboardTab !== 'all-stocks'}
        className="dashboard-tabpanel"
      >
      <div className="component">
        <div className="stocks-header">
          <h2>All Stocks</h2>
          <div className="filter-controls">
            <input
              type="text"
              placeholder="Search by ticker or name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
              style={{
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.9rem',
                width: '250px'
              }}
            />
            <select 
              id="sector-filter-all"
              value={sectorFilterAll} 
              onChange={(e) => {
                setSectorFilterAll(e.target.value);
                setCurrentPage(1);
              }}
              className="sector-filter"
              style={{ width: '180px' }}
            >
              {allSectors.map(sector => (
                <option key={sector} value={sector}>
                  {sector === 'all' ? 'All Sectors' : sector}
                </option>
              ))}
            </select>
            <select 
              id="market-cap-filter-all"
              value={allStocksMarketCapFilter} 
              onChange={(e) => {
                setAllStocksMarketCapFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="sector-filter"
              style={{ width: '200px' }}
            >
              {Object.entries(MARKET_CAP_INTERVALS).map(([key, interval]) => (
                <option key={key} value={key}>
                  {interval.label}
                </option>
              ))}
            </select>
            <select
              id="entries-per-page-all"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="entries-per-page-dropdown"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>

        {allStocksLoading ? (
          <div className="loading">Loading all stocks...</div>
        ) : allStocksError ? (
          <div className="error">{allStocksError}</div>
        ) : paginatedStocks.length === 0 ? (
          <p className="no-price">No stocks.</p>
        ) : (
          <>
            <div className="table-container">
              <table className="all-stocks-table">
                <thead>
                  <tr>
                    <th 
                      className="sortable-header"
                      onClick={() => handleSort('ticker')}
                    >
                      Ticker {sortColumn === 'ticker' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="sortable-header"
                      onClick={() => handleSort('name')}
                    >
                      Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="sortable-header"
                      onClick={() => handleSort('sector')}
                    >
                      Sector {sortColumn === 'sector' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="sortable-header"
                      onClick={() => handleSort('price')}
                    >
                      Price {sortColumn === 'price' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="sortable-header"
                      onClick={() => handleSort('market_cap')}
                      style={{ width: '120px', whiteSpace: 'nowrap' }}
                    >
                      Market Cap {sortColumn === 'market_cap' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="sortable-header tracked-column-header"
                      onClick={() => handleSort('tracked')}
                    >
                      Tracked {sortColumn === 'tracked' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStocks.map(stock => (
                    <tr key={stock.ticker} className="table-row">
                      <td className="table-cell">
                        <span 
                          style={{ cursor: 'pointer', color: '#3b82f6', fontWeight: 'bold' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/stock/${stock.ticker}`);
                          }}
                        >
                          {stock.ticker}
                        </span>
                      </td>
                      <td className="table-cell">{stock.name}</td>
                      <td className="table-cell">
                        {stock.sector && (
                          <span 
                            className="sector-badge"
                            style={{
                              backgroundColor: getSectorColor(stock.sector).bg,
                              color: getSectorColor(stock.sector).text,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              display: 'inline-block'
                            }}
                          >
                            {sectorAbbreviations[stock.sector] || stock.sector}
                          </span>
                        )}
                      </td>
                      <td className="table-cell">
                        {stock.current_price ? `$${stock.current_price.toFixed(2)}` : 'N/A'}
                      </td>
                      <td className="table-cell" style={{ width: '120px' }}>
                        {formatMarketCap(stock.market_cap)}
                      </td>
                      <td className="table-cell tracked-column-cell">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleTracked(stock.ticker, stock.tracked || false);
                          }}
                          className="tracked-toggle-button eye-icon-button"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'transform 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          {(stock.tracked || false) ? (
                            // Filled/highlighted eye icon with oval
                            <svg 
                              width="20" 
                              height="20" 
                              viewBox="-4 -4 32 32" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path 
                                d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" 
                                fill="#3b82f6"
                                stroke="#3b82f6"
                                strokeWidth="1.5"
                              />
                              <ellipse 
                                cx="12" 
                                cy="12" 
                                rx="13.5" 
                                ry="10.5" 
                                fill="none" 
                                stroke="#3b82f6" 
                                strokeWidth="2"
                              />
                            </svg>
                          ) : (
                            // Outline eye icon
                            <svg 
                              width="20" 
                              height="20" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path 
                                d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" 
                                fill="none"
                                stroke="#999"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {totalPages > 1 && (
              <div className="pagination-controls" style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid #f0f0f0'
              }}>
                <div className="pagination-arrows" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.9rem', color: '#666', marginRight: '0.5rem' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="pagination-arrow-button"
                    aria-label="Previous page"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="pagination-arrow-button"
                    aria-label="Next page"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </div>

      <div
        id="dashboard-panel-debug"
        role="tabpanel"
        aria-labelledby="dashboard-tab-debug"
        hidden={activeDashboardTab !== 'debug'}
        className="dashboard-tabpanel"
      >
        <DebugPipelineFreshness isActive={activeDashboardTab === 'debug'} />
        <DebugBulkIngest />
      </div>

      <div
        id="dashboard-panel-more"
        role="tabpanel"
        aria-labelledby="dashboard-tab-more"
        hidden={activeDashboardTab !== 'more'}
        className="dashboard-tabpanel"
      >
        <div className="component">
          <p className="no-price">Coming soon.</p>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
